import * as React from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MapPin } from 'lucide-react';
import { cn } from '../../utils';
import { CountrySelect, type CountryCode } from './country-select';
import { Input } from './input';
import { Label } from './label';
import { Switch } from './switch';
import { ValidationStatusIcon } from './validation-status';
import type { FrenchDepartmentCode } from './french-departments';
import type { SwissCanton } from './swiss-cantons';

export interface AddressValue {
  street: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  /** User selections are ISO codes; stored legacy values may be arbitrary strings. */
  country: string;
  canton?: SwissCanton;
  /** OFS/BFS commune number returned by GeoAdmin for a Swiss address. */
  communeBfs?: number;
  department?: FrenchDepartmentCode;
  /** Provider metadata. Consumers should not persist these fields as address data. */
  placeId?: string;
  coordinates?: {
    lat: number;
    lng: number;
  };
}

export interface AddressSuggestion<TValue extends AddressValue = AddressValue> {
  id: string;
  label: string;
  description?: string;
  /** Providers may include the resolved value to avoid a second request. */
  value?: TValue;
}

export interface AddressProviderContext {
  signal: AbortSignal;
  allowedCountries?: readonly CountryCode[];
}

export type AddressSuggestionsProvider<
  TSuggestion extends AddressSuggestion = AddressSuggestion,
> = (
  query: string,
  context: AddressProviderContext,
) => Promise<readonly TSuggestion[]>;

export type AddressDetailsResolver<
  TSuggestion extends AddressSuggestion = AddressSuggestion,
  TValue extends AddressValue = AddressValue,
> = (
  suggestion: TSuggestion,
  context: AddressProviderContext,
) => Promise<TValue>;

export interface AddressInputLabels {
  search: string;
  manualEntry: string;
  street: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: string;
  canton: string;
  department: string;
  searching: string;
  noOptions: string;
  /** Status text. Use `{count}` where the number should appear. */
  results: string;
  countrySearch: string;
  noCountries: string;
}

export interface AddressInputPlaceholders {
  search: string;
  street: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: string;
  canton: string;
  department: string;
}

export type AddressInputField =
  | 'search'
  | 'street'
  | 'streetNumber'
  | 'postalCode'
  | 'city'
  | 'country'
  | 'canton'
  | 'department';

export type AddressInputRequiredFields = Partial<
  Record<AddressInputField, boolean>
>;

type AddressTextField = 'street' | 'streetNumber' | 'postalCode' | 'city';

export type AddressInputFieldErrors = Partial<
  Record<AddressInputField | (string & {}), string | boolean>
>;

export interface AddressManualFieldsContext<
  TValue extends AddressValue = AddressValue,
> {
  value: TValue;
  disabled: boolean;
  required: boolean;
  isRequired: (field: AddressInputField) => boolean;
  disablePortal: boolean;
  baseId: string;
  name?: string;
  errors: AddressInputFieldErrors;
  labels: AddressInputLabels;
  placeholders: AddressInputPlaceholders;
  updateValue: (patch: Partial<TValue>) => void;
}

export interface AddressAutocompleteInputProps<
  TValue extends AddressValue = AddressValue,
  TSuggestion extends AddressSuggestion<TValue> = AddressSuggestion<TValue>,
> {
  value: TValue | null;
  onChange: (value: TValue | null) => void;
  suggestionsProvider?: AddressSuggestionsProvider<TSuggestion>;
  detailsResolver?: AddressDetailsResolver<TSuggestion, TValue>;
  labels?: Partial<AddressInputLabels>;
  placeholders?: Partial<AddressInputPlaceholders>;
  errors?: AddressInputFieldErrors;
  helperText?: string;
  debounceMs?: number;
  minimumQueryLength?: number;
  disabled?: boolean;
  required?: boolean;
  /** Override required state field by field. Unlisted fields use `required`. */
  requiredFields?: AddressInputRequiredFields;
  allowedCountries?: readonly CountryCode[];
  defaultCountry: CountryCode;
  fixedCountry?: CountryCode;
  autocompleteCountries?: readonly CountryCode[];
  locale?: string;
  name?: string;
  id?: string;
  /** Override the search input id while keeping `id` as the field id prefix. */
  searchId?: string;
  className?: string;
  onFetchError?: (error: unknown) => void;
  /** Called before a search edit, manual field edit, or suggestion selection. */
  onEdit?: () => void;
  /** Additional resolving state used by wrappers that enrich an initial value. */
  resolving?: boolean;
  /** Render suggestions and country menus next to their controls instead of in document.body. */
  disablePortal?: boolean;
  /** Open the structured fields on first render when a provider is configured. */
  defaultManual?: boolean;
  /** Keep the structured fields visible even if the user did not open them. */
  forceManual?: boolean;
  /** Add a country-specific field after the common address fields. */
  renderRegionField?: (
    context: AddressManualFieldsContext<TValue>,
  ) => React.ReactNode;
}

const DEFAULT_LABELS: AddressInputLabels = {
  search: 'Address',
  manualEntry: 'Enter the address manually',
  street: 'Street',
  streetNumber: 'Number',
  postalCode: 'Postal code',
  city: 'City',
  country: 'Country',
  canton: 'Canton',
  department: 'Département',
  searching: 'Searching...',
  noOptions: 'No address found',
  results: '{count} results',
  countrySearch: 'Search...',
  noCountries: 'No country found.',
};

const DEFAULT_PLACEHOLDERS: AddressInputPlaceholders = {
  search: 'Search for an address',
  street: 'Street',
  streetNumber: 'Number',
  postalCode: 'Postal code',
  city: 'City',
  country: 'Select country',
  canton: 'Select canton',
  department: 'Select département',
};

function emptyAddress(country: string): AddressValue {
  return {
    street: '',
    streetNumber: '',
    postalCode: '',
    city: '',
    country,
  };
}

function formatAddress(value: AddressValue): string {
  const streetParts =
    value.country === 'FR'
      ? [value.streetNumber, value.street]
      : [value.street, value.streetNumber];
  const street = streetParts.filter(Boolean).join(' ');
  const locality = [value.postalCode, value.city].filter(Boolean).join(' ');
  return [street, locality].filter(Boolean).join(', ');
}

function withoutProviderMetadata<TValue extends AddressValue>(
  value: TValue,
): TValue {
  const {
    placeId: _placeId,
    coordinates: _coordinates,
    ...manualValue
  } = value;
  return manualValue as TValue;
}

function withoutStaleSwissMetadata<TValue extends AddressValue>(
  value: TValue,
  previous: TValue,
  patch: Partial<TValue>,
): TValue {
  if (value.country.trim().toUpperCase() !== 'CH') return value;

  const changed = (field: AddressTextField) =>
    field in patch && value[field] !== previous[field];
  const next = { ...value };
  if (changed('street') || changed('postalCode') || changed('city')) {
    delete next.communeBfs;
  }
  if (changed('postalCode') || changed('city')) {
    delete next.canton;
  }
  return next;
}

function hasProviderMetadata(value: AddressValue | null): boolean {
  return Boolean(value?.placeId && value.coordinates);
}

function isAbortError(error: unknown, signal: AbortSignal): boolean {
  return (
    signal.aborted ||
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function FieldError({
  id,
  error,
}: {
  id: string;
  error: string | boolean | undefined;
}) {
  if (typeof error !== 'string') return null;
  return (
    <p id={id} className="text-sm text-destructive">
      {error}
    </p>
  );
}

function AddressAutocompleteInputInner<
  TValue extends AddressValue = AddressValue,
  TSuggestion extends AddressSuggestion<TValue> = AddressSuggestion<TValue>,
>(
  {
    value,
    onChange,
    suggestionsProvider,
    detailsResolver,
    labels: labelOverrides,
    placeholders: placeholderOverrides,
    errors = {},
    helperText,
    debounceMs = 300,
    minimumQueryLength = 3,
    disabled = false,
    required = false,
    requiredFields,
    allowedCountries,
    defaultCountry,
    fixedCountry,
    autocompleteCountries = [],
    locale = 'en',
    name,
    id,
    searchId,
    className,
    onFetchError,
    onEdit,
    resolving = false,
    disablePortal = false,
    defaultManual = false,
    forceManual = false,
    renderRegionField,
  }: AddressAutocompleteInputProps<TValue, TSuggestion>,
  ref: React.ForwardedRef<HTMLInputElement>,
) {
  const generatedId = React.useId();
  const baseId = id ?? `address-${generatedId.replace(/:/g, '')}`;
  const labels = React.useMemo(
    () => ({ ...DEFAULT_LABELS, ...labelOverrides }),
    [labelOverrides],
  );
  const placeholders = React.useMemo(
    () => ({ ...DEFAULT_PLACEHOLDERS, ...placeholderOverrides }),
    [placeholderOverrides],
  );
  const activeCountry = fixedCountry ?? value?.country ?? defaultCountry;
  const hasProvider = Boolean(suggestionsProvider);
  const [query, setQuery] = React.useState(() =>
    value ? formatAddress(value) : '',
  );
  const [manualMode, setManualMode] = React.useState(
    !hasProvider || defaultManual,
  );
  const manualVisible = !hasProvider || manualMode || forceManual;
  const [suggestions, setSuggestions] = React.useState<readonly TSuggestion[]>(
    [],
  );
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(0);
  const [verified, setVerified] = React.useState(hasProviderMetadata(value));
  const [queryDirty, setQueryDirty] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const abortRef = React.useRef<AbortController | null>(null);
  const previousCountryRef = React.useRef(activeCountry);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const listboxId = `${baseId}-suggestions`;
  const searchFieldId = searchId ?? baseId;
  const [dropdownStyle, setDropdownStyle] = React.useState<React.CSSProperties>(
    { visibility: 'hidden' },
  );

  React.useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, [open]);

  const updateDropdownPosition = React.useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const gap = 8;
    const maximumHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const placeAbove = spaceBelow < maximumHeight && rect.top > spaceBelow;

    if (disablePortal) {
      setDropdownStyle({
        position: 'absolute',
        ...(placeAbove
          ? { bottom: '100%', marginBottom: gap }
          : { top: '100%', marginTop: gap }),
        left: 0,
        width: '100%',
        zIndex: 9999,
      });
      return;
    }

    setDropdownStyle({
      position: 'fixed',
      ...(placeAbove
        ? { bottom: window.innerHeight - rect.top + gap }
        : { top: rect.bottom + gap }),
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
      pointerEvents: 'auto',
    });
  }, [disablePortal]);

  React.useLayoutEffect(() => {
    if (!open) return;
    updateDropdownPosition();
    if (disablePortal) return;
    window.addEventListener('scroll', updateDropdownPosition, true);
    window.addEventListener('resize', updateDropdownPosition);
    return () => {
      window.removeEventListener('scroll', updateDropdownPosition, true);
      window.removeEventListener('resize', updateDropdownPosition);
    };
  }, [disablePortal, open, updateDropdownPosition]);

  React.useEffect(() => {
    if (!open || !listRef.current) return;
    const option = listRef.current.querySelector(
      `[data-option-index="${highlightedIndex}"]`,
    ) as HTMLElement | null;
    option?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIndex, open]);

  React.useEffect(
    () => () => {
      abortRef.current?.abort();
      clearTimeout(debounceRef.current);
    },
    [],
  );

  React.useEffect(() => {
    if (previousCountryRef.current === activeCountry) return;
    previousCountryRef.current = activeCountry;
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    abortRef.current = null;
    setQuery('');
    setQueryDirty(false);
    setSuggestions([]);
    setHasSearched(false);
    setOpen(false);
    setLoading(false);
    setHighlightedIndex(0);
    setVerified(false);
    setManualMode(
      !autocompleteCountries.some((country) => country === activeCountry),
    );
  }, [activeCountry, autocompleteCountries]);

  const reportError = React.useCallback(
    (error: unknown, signal: AbortSignal) => {
      if (!isAbortError(error, signal)) onFetchError?.(error);
    },
    [onFetchError],
  );

  const runSearch = React.useCallback(
    async (nextQuery: string) => {
      if (!suggestionsProvider) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setHasSearched(false);
      setOpen(true);

      try {
        const results = await suggestionsProvider(nextQuery, {
          signal: controller.signal,
          allowedCountries,
        });
        if (controller.signal.aborted) return;
        setSuggestions(results);
        setHighlightedIndex(0);
        setHasSearched(true);
      } catch (error) {
        if (controller.signal.aborted) return;
        setSuggestions([]);
        setHasSearched(true);
        reportError(error, controller.signal);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [allowedCountries, reportError, suggestionsProvider],
  );

  const invalidateProviderValue = React.useCallback(() => {
    setVerified(false);
    if (value && (value.placeId || value.coordinates)) {
      onChange(withoutProviderMetadata(value));
    }
  }, [onChange, value]);

  const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextQuery = event.target.value;
    onEdit?.();
    setQuery(nextQuery);
    setQueryDirty(true);
    invalidateProviderValue();
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();
    setLoading(false);

    if (nextQuery.trim().length < minimumQueryLength) {
      setSuggestions([]);
      setHasSearched(false);
      setOpen(false);
      return;
    }

    debounceRef.current = setTimeout(
      () => void runSearch(nextQuery.trim()),
      debounceMs,
    );
  };

  const selectSuggestion = React.useCallback(
    async (suggestion: TSuggestion) => {
      onEdit?.();
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setOpen(false);
      setQuery(suggestion.label);
      setQueryDirty(true);
      setVerified(false);
      setLoading(true);
      if (value && (value.placeId || value.coordinates)) {
        onChange(withoutProviderMetadata(value));
      }

      try {
        let resolved = suggestion.value;
        if (!resolved) {
          if (!detailsResolver) {
            throw new Error(
              'Address suggestions must include a value when no details resolver is configured.',
            );
          }
          resolved = await detailsResolver(suggestion, {
            signal: controller.signal,
            allowedCountries,
          });
        }
        if (controller.signal.aborted) return;
        onChange(resolved);
        setVerified(true);
        setQueryDirty(false);
        setManualMode(false);
      } catch (error) {
        if (controller.signal.aborted) return;
        setVerified(false);
        reportError(error, controller.signal);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [allowedCountries, detailsResolver, onChange, onEdit, reportError, value],
  );

  const handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (!open || suggestions.length === 0) {
      if (event.key === 'Escape') setOpen(false);
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((index) =>
          Math.min(index + 1, suggestions.length - 1),
        );
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((index) => Math.max(index - 1, 0));
        break;
      case 'Enter': {
        event.preventDefault();
        const suggestion = suggestions[highlightedIndex];
        if (suggestion) void selectSuggestion(suggestion);
        break;
      }
      case 'Escape':
        event.preventDefault();
        setOpen(false);
        break;
    }
  };

  const currentValue = (value ?? emptyAddress(activeCountry)) as TValue;
  const searchValue = !queryDirty && value ? formatAddress(value) : query;
  const showVerified = !queryDirty && (verified || hasProviderMetadata(value));
  const isRequired = React.useCallback(
    (field: AddressInputField) => requiredFields?.[field] ?? required,
    [required, requiredFields],
  );

  const updateManualValue = React.useCallback(
    (patch: Partial<TValue>) => {
      onEdit?.();
      const base = value ?? (emptyAddress(activeCountry) as TValue);
      const next = withoutStaleSwissMetadata(
        withoutProviderMetadata({
          ...base,
          ...patch,
          country: activeCountry,
        }),
        base,
        patch,
      );
      setVerified(false);
      setQueryDirty(true);
      onChange(next);
    },
    [activeCountry, onChange, onEdit, value],
  );

  const handleCountryChange = React.useCallback(
    (nextCountry: string) => {
      const country = nextCountry as CountryCode;
      if (country === activeCountry) return;
      onEdit?.();
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
      abortRef.current = null;
      setQuery('');
      setQueryDirty(false);
      setSuggestions([]);
      setHasSearched(false);
      setOpen(false);
      setLoading(false);
      setHighlightedIndex(0);
      setVerified(false);
      setManualMode(!autocompleteCountries.includes(country));
      onChange(emptyAddress(country) as TValue);
    },
    [activeCountry, autocompleteCountries, onChange, onEdit],
  );

  const field = (
    fieldName: AddressTextField,
    inputRef?: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const fieldId = `${baseId}-${fieldName}`;
    const error = errors[fieldName];
    const errorId = `${fieldId}-error`;
    return (
      <div className="grid gap-1.5">
        <Label htmlFor={fieldId} required={isRequired(fieldName)}>
          {labels[fieldName]}
        </Label>
        <Input
          ref={inputRef}
          id={fieldId}
          name={name ? `${name}.${fieldName}` : undefined}
          value={currentValue[fieldName]}
          onChange={(event) =>
            updateManualValue({
              [fieldName]: event.target.value,
            } as Partial<TValue>)
          }
          placeholder={placeholders[fieldName]}
          disabled={disabled}
          required={isRequired(fieldName)}
          aria-invalid={Boolean(error)}
          aria-describedby={typeof error === 'string' ? errorId : undefined}
          error={Boolean(error)}
        />
        <FieldError id={errorId} error={error} />
      </div>
    );
  };

  const structuredFields = (
    <div className="grid gap-3" data-address-manual-fields>
      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_8rem]">
        {field('street', hasProvider ? undefined : ref)}
        {field('streetNumber')}
      </div>
      <div className="grid gap-3 sm:grid-cols-[10rem_minmax(0,1fr)]">
        {field('postalCode')}
        {field('city')}
      </div>
      {renderRegionField?.({
        value: currentValue,
        disabled,
        required,
        isRequired,
        disablePortal,
        baseId,
        name,
        errors,
        labels,
        placeholders,
        updateValue: updateManualValue,
      })}
    </div>
  );

  const countryField = (
    <div className="grid gap-1.5" data-address-country-field>
      <Label htmlFor={`${baseId}-country`} required={isRequired('country')}>
        {labels.country}
      </Label>
      <CountrySelect
        id={`${baseId}-country`}
        value={activeCountry}
        onChange={handleCountryChange}
        allowedCountries={fixedCountry ? [fixedCountry] : allowedCountries}
        locale={locale}
        placeholder={placeholders.country}
        searchPlaceholder={labels.countrySearch}
        noResultsText={labels.noCountries}
        disabled={disabled || Boolean(fixedCountry)}
        error={Boolean(errors.country)}
        disablePortal={disablePortal}
      />
      <FieldError id={`${baseId}-country-error`} error={errors.country} />
    </div>
  );

  const suggestionsDropdown = open ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="animate-fade-in overflow-hidden rounded-xl border-2 border-border bg-card shadow-hard"
    >
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        aria-busy={loading || resolving}
        className="max-h-60 overflow-y-auto p-1"
      >
        {loading && suggestions.length === 0 ? (
          <li
            role="presentation"
            className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground"
          >
            <Loader2 className="size-4 animate-spin" />
            {labels.searching}
          </li>
        ) : suggestions.length === 0 && hasSearched ? (
          <li
            role="presentation"
            className="px-3 py-2 text-sm text-muted-foreground"
          >
            {labels.noOptions}
          </li>
        ) : (
          suggestions.map((suggestion, index) => (
            <li
              key={suggestion.id}
              id={`${listboxId}-option-${index}`}
              role="option"
              aria-selected={index === highlightedIndex}
              data-option-index={index}
              onMouseEnter={() => setHighlightedIndex(index)}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => void selectSuggestion(suggestion)}
              className={cn(
                'flex cursor-pointer items-start gap-2 rounded-lg px-3 py-2 text-sm',
                index === highlightedIndex && 'bg-muted',
              )}
            >
              <MapPin className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0">
                <span className="block truncate">{suggestion.label}</span>
                {suggestion.description && (
                  <span className="block truncate text-xs text-muted-foreground">
                    {suggestion.description}
                  </span>
                )}
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div className={cn('grid gap-3', className)}>
      {countryField}
      {hasProvider && (
        <>
          <div className="grid gap-1.5">
            <Label
              htmlFor={searchFieldId}
              required={isRequired('search') && !manualVisible}
            >
              {labels.search}
            </Label>
            <div ref={containerRef} className={cn(disablePortal && 'relative')}>
              <Input
                ref={ref}
                id={searchFieldId}
                name={name}
                value={searchValue}
                onChange={handleQueryChange}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                  if (suggestions.length > 0 || hasSearched) setOpen(true);
                }}
                placeholder={placeholders.search}
                disabled={disabled}
                required={isRequired('search') && !manualVisible}
                error={Boolean(errors.search)}
                aria-invalid={Boolean(errors.search)}
                aria-describedby={
                  typeof errors.search === 'string'
                    ? `${searchFieldId}-error`
                    : undefined
                }
                autoComplete="off"
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                aria-activedescendant={
                  open && suggestions.length > 0
                    ? `${listboxId}-option-${highlightedIndex}`
                    : undefined
                }
                aria-busy={loading || resolving}
                startAdornment={<MapPin />}
                endAdornment={
                  loading || resolving ? (
                    <Loader2 data-address-loading className="animate-spin" />
                  ) : showVerified ? (
                    <span data-address-verified>
                      <ValidationStatusIcon status={{ type: 'valid' }} />
                    </span>
                  ) : undefined
                }
              />
              {suggestionsDropdown &&
                (disablePortal
                  ? suggestionsDropdown
                  : createPortal(suggestionsDropdown, document.body))}
            </div>
            <FieldError id={`${searchFieldId}-error`} error={errors.search} />
            <span className="sr-only" role="status" aria-live="polite">
              {loading || resolving
                ? labels.searching
                : hasSearched
                  ? labels.results.replace(
                      '{count}',
                      suggestions.length.toString(),
                    )
                  : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id={`${baseId}-manual`}
              checked={manualVisible}
              onCheckedChange={setManualMode}
              disabled={disabled}
              aria-label={labels.manualEntry}
            />
            <Label
              htmlFor={`${baseId}-manual`}
              className="normal-case tracking-normal"
            >
              {labels.manualEntry}
            </Label>
          </div>
        </>
      )}
      {manualVisible && structuredFields}
      {helperText && (
        <p
          className={cn(
            'text-sm',
            Object.values(errors).some(Boolean)
              ? 'text-destructive'
              : 'text-muted-foreground',
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}

const AddressAutocompleteInput = React.forwardRef(
  AddressAutocompleteInputInner,
) as <
  TValue extends AddressValue = AddressValue,
  TSuggestion extends AddressSuggestion<TValue> = AddressSuggestion<TValue>,
>(
  props: AddressAutocompleteInputProps<TValue, TSuggestion> &
    React.RefAttributes<HTMLInputElement>,
) => React.ReactElement;

export { AddressAutocompleteInput, emptyAddress, withoutProviderMetadata };
